/* ===========================================================================
   CALMY ORDER TRACKING — carrier detection
   ===========================================================================

   What this does and does not do.

   DOES: read a tracking number, work out which carrier issued it from the
   number's own format, and send the visitor to that carrier's tracking page.

   DOES NOT: fetch a status. No carrier exposes one without an account and a
   server-side key, and inventing one would be worse than saying nothing. The
   page is a router, and it is honest about being a router.

   The carrier list comes from section blocks, injected as JSON — so a merchant
   can add a carrier or correct a pattern in the theme editor. DEFAULTS below
   are a fallback for a freshly added section with no blocks yet; blocks always
   win, and a block with the same name replaces the default rather than
   duplicating it.

   Pattern notes, because these are easy to get subtly wrong:
     - ORDER IS SIGNIFICANT. The first pattern that matches wins, so specific
       prefixes must precede generic shapes. Omniva's S10 pattern
       ^[A-Z]{2}\\d{9}(LT|LV|EE)$ also matches CC123456789LT, which is a
       Lietuvos paštas number — with Omniva listed first, every LP parcel was
       routed to the wrong carrier. Named prefixes now come first.
     - Only prefixes that can be attributed with confidence are listed. EE is a
       shared S10 express class, not a Lietuvos paštas marker; claiming it sent
       Omniva parcels to the wrong site. Ambiguous prefixes deliberately fall
       through to the generic Baltic matcher, and the manual dropdown is the
       correction path when even that guesses wrong.
     - The bare digit runs cannot be told apart by format alone, so each takes
       one length and no more: 10 DHL, 11 Venipak, 12 GLS, 14 DPD, 15/20 FedEx.
       Overlapping ranges meant whichever sat higher silently claimed the rest.
     - DPD and Venipak both use bare digit runs of similar length, which cannot
       be told apart by format alone. Both are offered; DPD is listed first
       because it carries the larger share of Baltic parcels, and the manual
       dropdown exists precisely for when the guess is wrong.
     - 4PX and YunExpress issue numbers that end CN or start with their own
       prefixes; these cover the common Chinese-origin dropship formats.
   =========================================================================== */
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  "use strict";

  var DEFAULTS = [
    { name: "Lietuvos paštas", pattern: "^(CC|RR|CP|RA|RB)\\d{9}LT$", url: "https://www.post.lt/track-trace?barcode={code}" },
    { name: "LP Express", pattern: "^LP\\d{8,12}$", url: "https://lpexpress.lt/en/track?code={code}" },
    { name: "Omniva", pattern: "^[A-Z]{2}\\d{9}(LT|LV|EE)$", url: "https://omniva.lt/private/track?barcode={code}" },
    { name: "UPS", pattern: "^1Z[0-9A-Z]{16}$", url: "https://www.ups.com/track?tracknum={code}" },
    { name: "PostNL", pattern: "^3S[A-Z0-9]{9,13}$", url: "https://postnl.post/track/?barcode={code}" },
    { name: "YunExpress", pattern: "^YT\\d{13,16}$", url: "https://www.yuntrack.com/parcelTracking?id={code}" },
    { name: "4PX", pattern: "^4PX\\w+|^\\w+CN$", url: "https://track.4px.com/#/result/0/{code}" },
    { name: "DPD", pattern: "^\\d{14}$", url: "https://tracking.dpd.de/status/en_GB/parcel/{code}" },
    { name: "Venipak", pattern: "^\\d{11}$", url: "https://www.venipak.lt/en/track/?code={code}" },
    { name: "GLS", pattern: "^\\d{12}$", url: "https://gls-group.eu/EU/en/parcel-tracking?match={code}" },
    { name: "FedEx", pattern: "^(\\d{15}|\\d{20})$", url: "https://www.fedex.com/fedextrack/?trknbr={code}" },
    { name: "DHL Express", pattern: "^\\d{10}$", url: "https://www.dhl.com/global-en/home/tracking.html?tracking-id={code}" }
  ];

  function normalise(v) {
    /* Carriers print numbers with spaces and dashes; nobody types them back
       consistently. Strip everything that is not alphanumeric and uppercase
       it before matching or substituting. */
    return String(v || "").replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
  }

  function buildList(root) {
    var list = DEFAULTS.slice();
    var el = root.querySelector("[data-calmy-carriers]");
    if (!el) return list;
    var blocks;
    try { blocks = JSON.parse(el.textContent || "[]"); } catch (e) { blocks = []; }
    if (!blocks.length) return list;

    /* Blocks win. A block whose name matches a default replaces it, so
       correcting a pattern in the editor does not leave the old one matching
       first. Anything new is appended. */
    blocks.forEach(function (b) {
      if (!b || !b.name || !b.url) return;
      var i = list.findIndex(function (d) { return d.name.toLowerCase() === String(b.name).toLowerCase(); });
      if (i > -1) list[i] = b; else list.push(b);
    });
    return list;
  }

  function detect(code, list) {
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (!c.pattern) continue;
      var re;
      try { re = new RegExp(c.pattern); } catch (e) { continue; } // a bad pattern must not break the page
      if (re.test(code)) return c;
    }
    return null;
  }

  function urlFor(carrier, code) {
    /* Accepts {code} and {{code}}. Shopify rejects a doubled brace inside a
     JSON template setting — it reads it as a dynamic source — so the token is
     single-braced everywhere; the doubled form is tolerated for any URL pasted
     in from elsewhere. */
    return String(carrier.url).replace(/\{\{?\s*code\s*\}?\}/g, encodeURIComponent(code));
  }

  function init(root) {
    if (!root || root.__calmyTrack) return;
    root.__calmyTrack = true;

    var input = root.querySelector("[data-calmy-track-input]");
    var form = root.querySelector("[data-calmy-track-form]");
    var result = root.querySelector("[data-calmy-track-result]");
    var manual = root.querySelector("[data-calmy-track-manual]");
    var select = root.querySelector("[data-calmy-track-select]");
    var manualGo = root.querySelector("[data-calmy-track-manual-go]");
    if (!input || !form || !result) return;

    var list = buildList(root);

    function show(html) { result.hidden = false; result.innerHTML = html; }
    function hide() { result.hidden = true; result.innerHTML = ""; }

    /* ---- the busy state ----
       The lookup itself is local — a pattern match, then a link to the
       carrier — so this is deliberately brief. It exists for the two things
       it genuinely buys: a pressed control that looks pressed, and a second
       press that cannot fire while the first is still resolving. */
    var goBtn = root.querySelector("[data-calmy-track-go]");
    var busy = false;
    function setBusy(btn, on) {
      if (!btn) return;
      btn.classList.toggle("is-busy", on);
      btn.disabled = on;
      if (on) btn.setAttribute("aria-busy", "true");
      else btn.removeAttribute("aria-busy");
    }

    function go() {
      if (busy) return;
      var raw = input.value;
      var code = normalise(raw);
      if (!code) {
        manual.hidden = true;
        show('<p class="ctrack__msg">Enter the tracking number from your shipping email.</p>');
        input.focus();
        return;
      }

      var carrier = detect(code, list);
      if (!carrier) {
        /* Not a failure state — a narrower question. */
        manual.hidden = false;
        show('<p class="ctrack__msg">' + CT("track_unknown", "We could not tell which carrier issued [code].").split("[code]").join("<b>" + code + "</b>") + "</p>");
        if (select) select.focus();
        return;
      }

      manual.hidden = true;
      busy = true;
      setBusy(goBtn, true);
      window.setTimeout(function () { busy = false; setBusy(goBtn, false); }, 450);
      show(
        '<p class="ctrack__found">' + CT("track_looks_like", "Looks like [carrier].").split("[carrier]").join("<b>" + carrier.name + "</b>") + "</p>" +
        '<a class="cbtn cbtn--solid ctrack__open" href="' + urlFor(carrier, code) + '" target="_blank" rel="noopener noreferrer">' +
        CT("track_open", "Track with [carrier]").split("[carrier]").join(carrier.name) +
        '<span class="calmy-sr"> ' + CT("track_new_tab", "(opens in a new tab)") + '</span></a>'
      );
    }

    form.addEventListener("submit", function (e) { e.preventDefault(); go(); });

    input.addEventListener("input", function () { if (!input.value) { hide(); manual.hidden = true; } });

    if (manualGo && select) {
      manualGo.addEventListener("click", function () {
        var tpl = select.value;
        var code = normalise(input.value);
        if (!tpl) { select.focus(); return; }
        if (!code) { input.focus(); return; }
        window.open(String(tpl).replace(/\{\{?\s*code\s*\}?\}/g, encodeURIComponent(code)), "_blank", "noopener");
      });
    }

    /* ---- the order lookup ----
       WHAT THIS DOES AND DOES NOT DO. It validates the two fields and then
       hands the customer to Shopify's own account portal, which emails a
       one-time code before showing anything. It does NOT look an order up
       from its number: this shop runs new customer accounts, there is no
       endpoint for that, and a theme that answered "here is order #1234" to
       anyone who typed a number would be handing out other people's orders.

       The order number is remembered in the message rather than sent
       anywhere, because the portal has no field for it. Nothing is cleared:
       a customer who mistypes an email still has both values in front of
       them. */
    var ordForm = root.querySelector("[data-calmy-track-order-form]");
    if (ordForm) {
      var ordNum = root.querySelector("[data-calmy-track-order]");
      var ordMail = root.querySelector("[data-calmy-track-email]");
      var ordBtn = root.querySelector("[data-calmy-track-order-go]");
      var ordOut = root.querySelector("[data-calmy-track-order-result]");
      var ordBusy = false;

      function ordShow(html) { if (ordOut) { ordOut.hidden = false; ordOut.innerHTML = html; } }
      function mark(el, bad) {
        if (!el) return;
        if (bad) el.setAttribute("aria-invalid", "true");
        else el.removeAttribute("aria-invalid");
      }

      [ordNum, ordMail].forEach(function (el) {
        if (el) el.addEventListener("input", function () { mark(el, false); });
      });

      ordForm.addEventListener("submit", function (e) {
        e.preventDefault();
        if (ordBusy) return;

        var num = ordNum ? String(ordNum.value || "").trim() : "";
        var mail = ordMail ? String(ordMail.value || "").trim() : "";
        var mailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail);

        mark(ordNum, !num);
        mark(ordMail, !mailOk);

        if (!num || !mailOk) {
          ordShow('<p class="ctrack__msg">' + CT("track_order_missing",
            "Enter your order number and the email address you used at checkout.") + "</p>");
          (!num ? ordNum : ordMail).focus();
          return;
        }

        ordBusy = true;
        setBusy(ordBtn, true);

        var portal = (ordBtn && ordBtn.getAttribute("data-portal")) || "/account";
        ordShow('<p class="ctrack__found">' + CT("track_order_sending",
          "Taking you to sign in as [email]. We send a one-time code - no password to remember.")
          .split("[email]").join("<b>" + mail.replace(/[<>&]/g, "") + "</b>") + "</p>");

        window.setTimeout(function () {
          try { window.top.location.href = portal; }
          catch (err) { window.location.href = portal; }
        }, 600);
      });
    }

    /* ---- the subtitle follows the tab ---- */
    var sub = root.querySelector("[data-calmy-track-sub]");

    /* ---- tabs ---- */
    var tabs = [].slice.call(root.querySelectorAll("[data-calmy-track-tab]"));
    var panes = [].slice.call(root.querySelectorAll("[data-calmy-track-pane]"));
    function select_(name) {
      tabs.forEach(function (t) {
        var on = t.getAttribute("data-calmy-track-tab") === name;
        t.classList.toggle("is-on", on);
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.tabIndex = on ? 0 : -1;
      });
      panes.forEach(function (p) { p.hidden = p.getAttribute("data-calmy-track-pane") !== name; });
      if (sub) {
        var t = sub.getAttribute(name === "ord" ? "data-sub-order" : "data-sub-number");
        if (t) sub.textContent = t;
      }
    }
    tabs.forEach(function (t, i) {
      t.addEventListener("click", function () { select_(t.getAttribute("data-calmy-track-tab")); });
      t.addEventListener("keydown", function (e) {
        var d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = (i + d + tabs.length) % tabs.length;
        tabs[n].focus();
        select_(tabs[n].getAttribute("data-calmy-track-tab"));
      });
    });

    /* Deep link: /pages/tracking?number=XXX or ?order=1234 pre-fills and, for a
       tracking number, resolves immediately. This is what the account page's
       "Track" links point at. */
    try {
      var qs = new URLSearchParams(window.location.search);
      var n = qs.get("number") || qs.get("tracking");
      if (n) { input.value = n; go(); }
      else if (qs.get("order")) select_("ord");
    } catch (e) {}
  }

  function boot() {
    document.querySelectorAll(".ctrack").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
